import React, { useState, useEffect } from 'react';
import {
  Page,
  Layout,
  Card,
  TextStyle,
  Stack,
  Button,
  Icon,
  Badge,
  Banner,
  Text,
  Heading,
  SkeletonBodyText,
  SkeletonDisplayText,
  Modal,
  Tooltip
} from '@shopify/polaris';
import { CircleTickMajor, StarFilledMinor, StarOutlineMinor, InfoMinor } from '@shopify/polaris-icons';
import { useAuth } from '../contexts/AuthContext';
import { getFlexCreditPackages, purchaseFlexCreditPackage } from '../services/subscriptionService';
import { getUserCreditStatus } from '../services/creditService';
import { createCheckoutSession, redirectToCheckout } from '../services/stripeService';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { updateLandingPageCache } from '../services/landingPageService';

const FlexCredits = () => {
  const { user } = useAuth();
  const [packages, setPackages] = useState({});
  const [creditStatus, setCreditStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPackage, setProcessingPackage] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeSales, setActiveSales] = useState({
    credits: { active: false, name: '', percentage: 0 }
  });
  const [explanationModalOpen, setExplanationModalOpen] = useState(false);
  const [currentExplanation, setCurrentExplanation] = useState('');

  // Fetch flex credit packages and user's credit status
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch flex credit packages
        const packagesData = await getFlexCreditPackages();
        
        // Sort packages by sortOrder
        const sortedPackages = Object.fromEntries(
          Object.entries(packagesData)
            .filter(([_, pack]) => pack.enabled)
            .sort((a, b) => a[1].sortOrder - b[1].sortOrder)
        );
        
        setPackages(sortedPackages);
        
        // Fetch active sales
        const salesDoc = await getDoc(doc(db, 'settings', 'sales'));
        if (salesDoc.exists()) {
          const salesData = salesDoc.data();
          setActiveSales(salesData);
          
          // Update the landing page cache with the flex credit package data
          updateLandingPageCache(null, sortedPackages, null, salesData);
        }
        
        // Fetch user credit status if logged in
        if (user) {
          const userCreditStatus = await getUserCreditStatus(user.uid);
          setCreditStatus(userCreditStatus);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching flex credit data:', err);
        setError('Failed to load flex credit packages. Please try again later.');
        setLoading(false);
      }
    };
    
    fetchData();
  }, [user]);

  // Fetch active sales
  const fetchActiveSales = async () => {
    try {
      // Get sales document from Firestore
      const salesRef = doc(db, 'settings', 'sales');
      const salesDoc = await getDoc(salesRef);
      
      if (salesDoc.exists()) {
        const salesData = salesDoc.data();
        setActiveSales({
          credits: salesData.credits || { active: false, name: '', percentage: 0 }
        });
      }
    } catch (error) {
      console.error('Error fetching active sales:', error);
    }
  };

  useEffect(() => {
    fetchActiveSales();
  }, []);

  // Handle flex credit package purchase
  const handlePurchasePackage = async (packageType) => {
    if (!user) return;
    
    try {
      setProcessingPackage(packageType);
      setError('');
      
      // Create a Stripe checkout session for the flex credits package
      const baseUrl = window.location.origin;
      const successUrl = `${baseUrl}/app/credits?success=true&package=${packageType}`;
      const cancelUrl = `${baseUrl}/app/credits?canceled=true`;
      
      const result = await createCheckoutSession(user.uid, { packageType }, successUrl, cancelUrl);
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to create checkout session');
      }
      
      // Redirect to Stripe checkout
      if (result.url) {
        console.log('Using URL from result for checkout:', result.url);
        window.location.href = result.url;
      } else if (result.sessionId) {
        console.log('Using sessionId for checkout:', result.sessionId);
        // Use the direct URL approach since Stripe.js can't be loaded due to CSP restrictions
        // Add the hash fragment to match the working subscription URL format
        window.location.href = `https://checkout.stripe.com/c/pay/${result.sessionId}#fidkdWxOYHwnPyd1blpxYHZxWjA0VzB3c0lNTlJzSGN3f1JUTEM9YXB1UERARz0zTFwzPWBfTVxiQ1Z8amJfS29Ra1BERExjNlFHUE1DTG1pUDM9SnVGUlw8f3JSZkRGazBhd1NqM05fSl1ONTVtRkNfTUM1aycpJ2N3amhWYHdzYHcnP3F3cGApJ2lkfGpwcVF8dWAnPyd2bGtiaWBabHFgaCcpJ2BrZGdpYFVpZGZgbWppYWB3dic%2FcXdwYHgl`;
      } else {
        throw new Error('No valid checkout URL or session ID found in the response');
      }
    } catch (err) {
      console.error('Error purchasing flex credits:', err);
      setError('Failed to initiate purchase process. Please try again later.');
      setProcessingPackage(null);
    }
  };

  // Check for URL parameters indicating return from Stripe checkout
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const success = queryParams.get('success');
    const packageType = queryParams.get('package');
    const canceled = queryParams.get('canceled');
    
    if (success === 'true' && packageType && user) {
      // Handle successful flex credits purchase
      const refreshCredits = async () => {
        try {
          setLoading(true);
          setError('');
          
          // Add a delay to ensure Stripe webhook has processed
          await new Promise(resolve => setTimeout(resolve, 2000));
          
          // Fetch package data if not loaded yet
          if (Object.keys(packages).length === 0) {
            const packagesData = await getFlexCreditPackages();
            const sortedPackages = Object.fromEntries(
              Object.entries(packagesData)
                .filter(([_, pack]) => pack.enabled)
                .sort((a, b) => a[1].sortOrder - b[1].sortOrder)
            );
            setPackages(sortedPackages);
          }
          
          // Refresh user credit status
          const userCreditStatus = await getUserCreditStatus(user.uid);
          setCreditStatus(userCreditStatus);
          
          // Show success message
          setSuccess(`Successfully purchased ${packages[packageType]?.name || packageType} credits!`);
          
          // Clear the URL parameters
          window.history.replaceState({}, document.title, '/app/credits');
          
          setLoading(false);
        } catch (err) {
          console.error('Error refreshing credit data:', err);
          setError('There was an issue verifying your purchase. Please contact support if the problem persists.');
          setLoading(false);
        }
      };
      
      refreshCredits();
    } else if (canceled === 'true') {
      // Handle canceled checkout
      setError('Credit purchase was canceled. No changes were made to your account.');
      
      // Clear the URL parameters
      window.history.replaceState({}, document.title, '/app/credits');
    }
  }, [user, packages]);

  // CSS styles for consistent card layout
  const styles = {
    cardWrapper: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    card: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
    },
    iconContainer: {
      height: '24px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      marginBottom: '0.5rem',
      gap: '4px'
    },
    badgeContainer: {
      margin: '0.5rem 0',
      height: '28px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    },
    featureList: {
      marginBottom: '1.5rem',
      flex: 1,
      minHeight: '100px' // Fixed height for feature list
    },
    featureItem: {
      display: 'flex',
      alignItems: 'center',
      minHeight: '30px'
    },
    checkIcon: {
      color: '#008060',
      width: '20px',
      textAlign: 'center',
      marginRight: '8px'
    },
    buttonContainer: {
      textAlign: 'center',
      marginTop: 'auto',
      paddingTop: '1rem'
    },
    packageHeader: {
      textAlign: 'center',
      marginBottom: '1rem',
      minHeight: '120px', // Fixed height for the package header section
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start'
    },
    bestValueBadge: {
      height: '28px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    }
  };

  // Render feature with checkmark
  const renderFeature = (feature) => {
    return (
      <div style={styles.featureItem}>
        <div style={styles.checkIcon}>
          <Icon source={CircleTickMajor} color="success" />
        </div>
        <TextStyle>{feature}</TextStyle>
      </div>
    );
  };

  // Helper function to render star icons based on package level
  const renderStars = (packageType) => {
    // Extract package number from package type (e.g., 'flex_3' -> 3)
    const packageNumber = parseInt(packageType.split('_')[1]) || 1;
    
    return (
      <div style={{ display: 'flex', justifyContent: 'center', gap: '2px' }}>
        {Array(5).fill().map((_, index) => (
          <Icon 
            key={index} 
            source={index < packageNumber ? StarFilledMinor : StarOutlineMinor} 
            color="warning" 
          />
        ))}
      </div>
    );
  };

  // Render package cards
  const renderPackageCards = () => {
    if (loading) {
      return Array(3).fill().map((_, index) => (
        <Layout.Section oneThird key={`skeleton-${index}`}>
          <Card sectioned>
            <SkeletonDisplayText size="small" />
            <div style={{ marginTop: '1rem' }}>
              <SkeletonBodyText lines={3} />
            </div>
          </Card>
        </Layout.Section>
      ));
    }
    
    if (Object.keys(packages).length === 0) {
      return (
        <Layout.Section>
          <Banner status="info">
            <p>No flex credit packages are available at this time. Please check back later.</p>
          </Banner>
        </Layout.Section>
      );
    }
    
    return Object.entries(packages).map(([packageType, pack]) => {
      const isProcessing = processingPackage === packageType;
      // Explicitly set Large Pack (flex_3) as the best value
      const isBestValue = packageType === 'flex_3';

      return (
        <Layout.Section oneThird key={packageType}>
          <div style={styles.cardWrapper}>
            <Card
              sectioned
              style={{
                ...styles.card,
                boxShadow: isBestValue ? '0 0 10px rgba(0, 128, 96, 0.3)' : '',
              }}
            >
              <div style={styles.packageHeader}>
                <div style={styles.iconContainer}>
                  {renderStars(packageType)}
                </div>
                <Heading element="h2">{pack.name}</Heading>
                <div style={styles.bestValueBadge}>
                  {isBestValue && (
                    <Badge status="success">BEST VALUE</Badge>
                  )}
                </div>
                {pack.description && (
                  <div style={{ marginTop: '0.25rem', minHeight: '30px' }}>
                    <TextStyle variation="subdued">{pack.description}</TextStyle>
                  </div>
                )}
                <div style={{ margin: '1rem 0' }}>
                  <Text variant="headingXl" as="p">
                    ${pack.price}
                  </Text>
                  {pack.compareAtPrice && activeSales.credits.active && (
                    <div style={{ marginTop: '0.25rem' }}>
                      <Text variant="bodyMd" as="p" color="subdued">
                        <s>${pack.compareAtPrice}</s> <Text variant="bodyMd" as="span" color="critical">{activeSales.credits.percentage}% off</Text>
                      </Text>
                    </div>
                  )}
                </div>
                <div style={styles.badgeContainer}>
                  <Badge status="info">{pack.credits} Credits</Badge>
                </div>
                
                {/* Credit count text with explanation icon */}
                {pack.creditCountText && (
                  <div style={{ marginTop: '0.5rem', display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                    <Text variant="bodyMd" as="p">
                      {pack.creditCountText}
                      {pack.creditCountExplanation && (
                        <Button
                          plain
                          monochrome
                          icon={<span style={{ fontSize: '16px', marginLeft: '4px' }}>&#x24D8;</span>}
                          onClick={() => {
                            setCurrentExplanation(pack.creditCountExplanation);
                            setExplanationModalOpen(true);
                          }}
                          accessibilityLabel="View credit explanation"
                        />
                      )}
                    </Text>
                  </div>
                )}
              </div>
              
              <div style={styles.featureList}>
                {renderFeature('One-time purchase')}
                {renderFeature('Never expires')}
                {renderFeature('Use for any product edits')}
              </div>
              
              <div style={styles.buttonContainer}>
                <Button
                  primary={isBestValue}
                  onClick={() => handlePurchasePackage(packageType)}
                  loading={isProcessing}
                  disabled={isProcessing || !user}
                >
                  Purchase Credits
                </Button>
              </div>
            </Card>
          </div>
        </Layout.Section>
      );
    });
  };

  // Show explanation modal when info icon is clicked
  const handleShowExplanation = (explanation) => {
    setCurrentExplanation(explanation);
    setExplanationModalOpen(true);
  };

  // Render explanation modal
  const renderExplanationModal = (
    <Modal
      open={explanationModalOpen}
      onClose={() => setExplanationModalOpen(false)}
      title="How We Calculate Product Edits"
    >
      <Modal.Section>
        <div 
          className="html-content-container"
          style={{ padding: '0 20px' }}
          dangerouslySetInnerHTML={{ __html: currentExplanation }}
        />
      </Modal.Section>
    </Modal>
  );

  return (
    <Page title="Flex Credits">
      {error && (
        <Banner status="critical" title="Error" onDismiss={() => setError('')}>
          {error}
        </Banner>
      )}
      
      {success && (
        <Banner status="success" title="Success" onDismiss={() => setSuccess('')}>
          {success}
        </Banner>
      )}
      
      <Layout>
        <Layout.Section>
          <Card sectioned>
            <Stack vertical spacing="loose">
              <Stack.Item>
                <TextStyle variation="subdued">
                  Purchase flex credits to use when your monthly credits are depleted. Flex credits never expire.
                </TextStyle>
              </Stack.Item>
              
              {/* Display sale banner if active */}
              {activeSales.credits.active && (
                <Stack.Item>
                  <Banner
                    title={activeSales.credits.name}
                    status="warning"
                  >
                    <p>Save {activeSales.credits.percentage}% on all flex credit packages for a limited time!</p>
                  </Banner>
                </Stack.Item>
              )}
              
              {creditStatus && (
                <Stack.Item>
                  <Banner status="info">
                    <p>You currently have <strong>{creditStatus.credits?.flex || 0}</strong> flex credits available.</p>
                  </Banner>
                </Stack.Item>
              )}
            </Stack>
          </Card>
        </Layout.Section>
      </Layout>
      
      {/* Package cards in a single row */}
      <div style={{ marginTop: '20px' }}>
        <Layout>
          {renderPackageCards()}
        </Layout>
      </div>
      
      {/* How credits work section */}
      <div style={{ marginTop: '20px' }}>
        <Layout>
          <Layout.Section>
            <Card sectioned>
              <Stack vertical spacing="tight">
                <Stack.Item>
                  <Heading element="h3">How Flex Credits Work</Heading>
                </Stack.Item>
                <Stack.Item>
                  <p>Flex credits are used automatically when you've used all your monthly subscription credits. They never expire and can be used for any product edits — big or small.</p>
                </Stack.Item>
                <Stack.Item>
                  <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                    <li>Product edits vary in credit cost depending on the content generated (e.g. title, description, benefits, SEO, images)</li>
                    <li>On average, a basic product edit (title, description, specifications) costs ~7 credits</li>
                    <li>More advanced edits (benefits, SEO, image processing) may cost up to 20 credits</li>
                    <li>Purchase Flex Credits anytime — no plan change required</li>
                    <li>Combine with your subscription for total flexibility</li>
                    <li>Perfect for seasonal updates, launch campaigns, or overflow work</li>
                  </ul>
                </Stack.Item>
                <Stack.Item>
                  <Button onClick={() => handleShowExplanation('This is a test explanation')}>
                    Show Explanation
                  </Button>
                </Stack.Item>
              </Stack>
            </Card>
          </Layout.Section>
        </Layout>
      </div>
      {renderExplanationModal}
    </Page>
  );
};

export default FlexCredits;
